import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useResizeObserver } from '@vestigas/utils';

import {
  computeBadgeOverflow,
  type FilterBadge,
} from './savedFiltersBadgeRowUtils';

/** Keep in sync with the `gap-2` class on the badge-row container. */
const BADGE_ROW_GAP_PX = 8;

const RESIZE_OBSERVER_OPTIONS: ResizeObserverOptions = { box: 'border-box' };

function captureMeasuredWidths(
  badgeElements: ReadonlyMap<string, HTMLDivElement>,
  widthsById: Map<string, number>,
): void {
  for (const [badgeId, element] of badgeElements) {
    widthsById.set(badgeId, element.offsetWidth);
  }
}

function areVisibleIdsEqual(
  previous: readonly string[],
  next: readonly string[],
): boolean {
  return (
    previous.length === next.length &&
    previous.every((id, index) => id === next[index])
  );
}

/**
 * Measures the badge row and decides which badges fit as visible tabs and
 * which move into the searchable overflow menu.
 *
 * Two-pass layout: while any badge width is unknown, every badge renders
 * (clipped by the container's `overflow-hidden`) so its width can be captured,
 * then the fitting partition is applied. In environments without layout
 * (jsdom unit tests) all widths stay 0 and the hook falls back to showing
 * every badge with no menu button.
 */
export function useBadgeRowOverflow({
  badges,
  selectedFilterId,
}: {
  readonly badges: readonly FilterBadge[];
  readonly selectedFilterId: string;
}) {
  const [containerElement, setContainerElement] = useState<HTMLDivElement>();
  const [visibleIds, setVisibleIds] = useState<readonly string[] | null>(null);
  const badgeElementsRef = useRef(new Map<string, HTMLDivElement>());
  const badgeWidthsRef = useRef(new Map<string, number>());
  const addControlRef = useRef<HTMLDivElement | null>(null);
  const menuButtonRef = useRef<HTMLDivElement | null>(null);

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    setContainerElement(node ?? undefined);
  }, []);

  const registerBadgeElement = useCallback(
    (badgeId: string) => (node: HTMLDivElement | null) => {
      if (node) {
        badgeElementsRef.current.set(badgeId, node);
      } else {
        badgeElementsRef.current.delete(badgeId);
      }
    },
    [],
  );

  const recompute = useCallback(() => {
    // Widths of hidden badges stay cached from when they were last rendered.
    captureMeasuredWidths(badgeElementsRef.current, badgeWidthsRef.current);

    const containerWidth = containerElement?.clientWidth ?? 0;
    const addControlWidth = addControlRef.current?.offsetWidth ?? 0;
    // Before the menu button first renders its width is unknown; the add
    // button is built identically (same IconButton size and wrapper), so its
    // width is a safe stand-in. Being off by a few px only moves one badge
    // into the menu earlier.
    const menuButtonWidth =
      menuButtonRef.current?.offsetWidth || addControlWidth;

    const hasUnmeasuredBadge = badges.some(
      (badge) => !badgeWidthsRef.current.get(badge.id),
    );

    if (containerWidth > 0 && hasUnmeasuredBadge) {
      // Render everything once (clipped) so all badges can be measured.
      setVisibleIds(null);
      return;
    }

    const { visibleBadges } = computeBadgeOverflow({
      addControlWidth,
      badges,
      containerWidth,
      gap: BADGE_ROW_GAP_PX,
      menuButtonWidth,
      selectedFilterId,
      widthById: badgeWidthsRef.current,
    });

    const nextVisibleIds = visibleBadges.map((badge) => badge.id);

    // Referential guard: only update state when the partition actually
    // changed, so the every-commit layout effect below cannot loop.
    setVisibleIds((previous) =>
      previous !== null && areVisibleIdsEqual(previous, nextVisibleIds)
        ? previous
        : nextVisibleIds,
    );
  }, [badges, containerElement, selectedFilterId]);

  // Runs after every commit: measures the freshly rendered badges and applies
  // (or re-applies) the fitting partition.
  useLayoutEffect(recompute);

  useResizeObserver(containerElement, RESIZE_OBSERVER_OPTIONS, recompute);

  // Widths captured before the webfont swaps in are measured against the
  // fallback font; the container never changes size, so the resize observer
  // does not catch it. Re-measure once the fonts settle.
  useEffect(() => {
    // jsdom does not implement FontFaceSet, so `document.fonts` is absent
    // there despite the DOM types declaring it as always present.
    const { fonts } = document as Partial<Document>;
    if (!fonts) return;

    let isActive = true;

    void fonts.ready.then(() => {
      if (isActive) recompute();
    });

    return () => {
      isActive = false;
    };
  }, [recompute]);

  const { hiddenBadges, visibleBadges } = useMemo(() => {
    if (visibleIds === null) {
      return { hiddenBadges: [] as FilterBadge[], visibleBadges: [...badges] };
    }

    const badgeById = new Map(badges.map((badge) => [badge.id, badge]));
    const visibleIdSet = new Set(visibleIds);

    return {
      hiddenBadges: badges.filter((badge) => !visibleIdSet.has(badge.id)),
      visibleBadges: visibleIds
        .map((id) => badgeById.get(id))
        .filter((badge): badge is FilterBadge => badge !== undefined),
    };
  }, [badges, visibleIds]);

  return {
    addControlRef,
    containerRef,
    hiddenBadges,
    menuButtonRef,
    registerBadgeElement,
    visibleBadges,
  };
}
